import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useDelete, useNotify } from "react-admin";
import { AlertType, CHILD_TYPE_LABELS, DeleteEntry, REASON_CODE_MESSAGES, ReasonCode, ResourceName } from "@/types";
import { featureFlags } from "@/utils/featureFlags";
import { ButtonStyleType } from "@/types/shared/uiComponents";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { useDeleteImpact } from "../hooks/useDeleteImpact";
import { useCascadeDeleteController } from "../CascadeDeleteProvider";
import { useCascadeDeletePermission } from "../hooks/useCascadeDeletePermission";

/**
 * Master switch for the cascade ("delete document + related records") action,
 * sourced from featureFlags (VITE_CASCADE_DOCUMENT_DELETE_ENABLED). OFF until the
 * write cascade (#5753) lands. Enabling must ALSO gate on the destructive-cascade
 * capability (#5752), not this flag alone — see the button's disabled expression.
 */
const CASCADE_ENABLED = featureFlags.cascadeDocumentDeleteEnabled;

/**
 * Volume cap: how many entries a section renders before collapsing the rest
 * into a "+N more" line. The server may return more; the section-level `*_count`
 * is the source of truth for the overflow tally, so we never map the full array
 * unbounded even if the backend forgets to cap it.
 */
const MAX_ENTRIES_PER_SECTION = 15;

/** Naive fallback pluralizer for child keys not in CHILD_TYPE_LABELS (@/types). */
const fallbackChildLabel = (key: string, count: number): string => {
  const base = formatToTitleCase(key).toLowerCase();
  if (count === 1) return base.replace(/s$/, "");
  return base;
};

/**
 * Build a compact, one-line summary of an entry's rolled-up children, e.g.
 * "12 claims · 2 reimbursements · 1 stop-loss claim". Zero counts are dropped;
 * an entry with no children returns an empty string (caller shows just the
 * label).
 */
const childSummary = (children?: Record<string, number>): string => {
  if (!children) return "";
  return Object.entries(children)
    .filter(([, count]) => count > 0)
    .map(([key, count]) => {
      const labels = CHILD_TYPE_LABELS[key];
      const noun = labels ? (count === 1 ? labels.one : labels.other) : fallbackChildLabel(key, count);
      return `${count} ${noun}`;
    })
    .join(" · ");
};

const reasonMessage = (reason?: string): string | undefined => {
  if (!reason) return undefined;
  return REASON_CODE_MESSAGES[reason as ReasonCode] ?? reason;
};

interface DeleteImpactDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  /** Optional label to make the confirmation copy concrete. */
  documentName?: string;
  /**
   * Fired after the SYNCHRONOUS "delete document only" path succeeds. The caller
   * owns the side effect — the queue refreshes its list; the show page navigates
   * away. (Kept out of the dialog so a show-page refresh doesn't refetch the
   * record that was just deleted.) NOT used for cascade: that path is fire-and-
   * forget (see onCascadeStarted) — its completion is handled by the provider's
   * background toast + cache invalidation, never a forced navigation.
   */
  onDeleted?: () => void;
  /**
   * Fired the instant a CASCADE delete is dispatched (before the dialog closes),
   * so the caller can react to the hand-off — e.g. the show page navigates to the
   * list right away while the delete runs in the background (#6141). The queue
   * leaves this unset and stays put; the background toast reports progress and the
   * provider's cache invalidation refreshes the list on completion.
   */
  onCascadeStarted?: () => void;
  /**
   * When false, present a **plain document delete** — no cascade option and no
   * impact preview (the Delete / Keep-and-scrub / Blocked sections are hidden and
   * the impact fetch is skipped). Used where cascade is never the intent and the
   * three-section preview only confuses, e.g. deleting a single-record attachment
   * from `DocumentsPanel` (#6507). Defaults to `true` — the document show page and
   * queue keep the full impact dialog.
   */
  allowCascade?: boolean;
}

type SectionKey = "delete" | "keep" | "blocked";

/**
 * Three-way delete-impact dialog for a document (#5751, ADR-078).
 *
 * On open it fetches the read-only impact preview and renders three collapsible
 * sections — Delete / Keep-and-scrub / Blocked. Each section is CLAIMANT-ROOTED
 * and summarized: every entry shows its human `label` plus a one-line rollup of
 * child counts ("12 claims · 2 reimbursements"). No aegle-ids, no per-record
 * drill-down. Sections cap at MAX_ENTRIES_PER_SECTION with a "+N more" line.
 * Blocked entries also surface a user-facing reason. The reviewer then chooses:
 *   - Delete document + related records (cascade) — disabled while blocked, or
 *     until the write cascade lands (CASCADE_ENABLED).
 *   - Delete document only — today's behavior; created records remain.
 *   - Cancel.
 *
 * Composed off ReprocessDialog (self-contained MUI Dialog, fetch-on-open,
 * spinner while loading, multi-button DialogActions).
 */
export const DeleteImpactDialog = ({
  open,
  onClose,
  documentId,
  documentName,
  onDeleted,
  onCascadeStarted,
  allowCascade = true,
}: DeleteImpactDialogProps) => {
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const [deleteOne] = useDelete();
  const [isDeleting, setIsDeleting] = useState(false);
  const { hasCapability: hasCascadeCapability, isLoading: permissionsLoading } = useCascadeDeletePermission();
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({
    delete: true,
    keep: false,
    blocked: true,
  });

  // Skip the impact fetch entirely when cascade isn't offered — there are no
  // sections to populate and no cascade decision to inform.
  const { data, isLoading, error } = useDeleteImpact({ documentId, enabled: open && allowCascade });

  const blocked: DeleteEntry[] = useMemo(() => data?.blocked ?? [], [data]);
  const isBlocked = blocked.length > 0;

  const blockedReasons = useMemo(() => {
    const seen = new Set<string>();
    blocked.forEach((b) => {
      const msg = reasonMessage(b.reason);
      if (msg) seen.add(msg);
    });
    return Array.from(seen);
  }, [blocked]);

  const toggle = (key: SectionKey) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const { start: startCascade } = useCascadeDeleteController();

  // Fire-and-forget cascade (#6141): hand the operation to the background
  // controller, let the caller react to the hand-off (the show page navigates
  // away), and close the dialog immediately so the user is never trapped watching
  // a spinner. Progress + the terminal result are reported by the provider's
  // background toast; completion refreshes lists via the provider's cache
  // invalidation — no callback fires here, since the user may have moved on.
  const handleStartCascade = useCallback(() => {
    const started = startCascade(documentId, { label: documentName });
    if (!started) {
      // No provider handled the request. Real app always mounts CascadeDeleteProvider in
      // Layout, so this is a defensive guard — but do NOT navigate away / close as if it
      // worked (that would present a destructive action as done when nothing was deleted,
      // #6141 review). Surface the failure and keep the dialog open instead.
      notify("Couldn't start the delete. Please refresh and try again.", { type: AlertType.ERROR });
      return;
    }
    onCascadeStarted?.();
    onClose();
  }, [startCascade, documentId, documentName, onCascadeStarted, onClose, notify]);

  // Only the SYNCHRONOUS document-only delete blocks the dialog now; the cascade
  // hands off and closes, so it never holds the dialog open.
  const busy = isDeleting;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  // Document-only: react-admin useDelete keeps cache invalidation. Pessimistic
  // mode so the delete actually runs before we act on success (the show page
  // navigates away; no optimistic removal to reconcile).
  const handleDeleteDocumentOnly = () => {
    setIsDeleting(true);
    void deleteOne(
      ResourceName.DOCUMENTS,
      { id: documentId },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          setIsDeleting(false);
          notify("Document deleted successfully", { type: AlertType.SUCCESS });
          onDeleted?.();
          onClose();
        },
        onError: (err) => {
          setIsDeleting(false);
          notifyError(err);
        },
      }
    );
  };

  // Cascade must satisfy ALL THREE gates: the kill-switch flag (CASCADE_ENABLED),
  // the server-enforced capability (#5752), and a non-blocked preview. Still
  // resolving permissions counts as not-allowed (fail closed, no flash).
  const cascadeAllowed = CASCADE_ENABLED && hasCascadeCapability && !isBlocked && !permissionsLoading;

  // Tooltip reason is ordered by specificity so the user sees the most concrete
  // cause: blocked-by-data first, then feature-off (nobody can), then a per-user
  // capability gap. While permissions load we show nothing (the button is
  // disabled anyway) so the "ask an administrator" copy doesn't flash.
  const cascadeDisabledReason = isBlocked
    ? blockedReasons.length > 0
      ? `Cascade delete is blocked: ${blockedReasons.join(" ")}`
      : "Cascade delete is blocked by one or more related records."
    : !CASCADE_ENABLED
      ? "Related-record delete is coming soon."
      : permissionsLoading
        ? undefined
        : !hasCascadeCapability
          ? "You don't have permission to delete related records. Ask an administrator to grant it."
          : undefined;

  /** One claimant-rooted entry: bold label + compact child-count summary. */
  const renderEntry = (entry: DeleteEntry, isBlockedSet: boolean) => {
    const removes = childSummary(entry.children);
    const keeps = childSummary(entry.children_kept);
    const reason = isBlockedSet ? reasonMessage(entry.reason) : undefined;
    return (
      <Box key={entry.id || entry.label} sx={{ mb: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {entry.label}
        </Typography>
        {removes && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Removing {removes}
          </Typography>
        )}
        {keeps && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Keeping {keeps}
          </Typography>
        )}
        {reason && (
          <Typography variant="caption" color="error" sx={{ display: "block" }}>
            {reason}
          </Typography>
        )}
      </Box>
    );
  };

  const renderSection = (
    key: SectionKey,
    title: string,
    entries: DeleteEntry[],
    count: number,
    isBlockedSet: boolean
  ) => {
    const shown = entries.slice(0, MAX_ENTRIES_PER_SECTION);
    const overflow = Math.max(count - shown.length, 0);
    return (
      <Box sx={{ mb: 1.5 }}>
        <Box
          onClick={() => toggle(key)}
          sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", py: 0.5 }}
        >
          <IconButton size="small" aria-label={expanded[key] ? "Collapse" : "Expand"}>
            {expanded[key] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {title}
          </Typography>
          <Chip
            size="small"
            label={count}
            color={isBlockedSet ? "error" : key === "delete" ? "default" : "info"}
            variant={count === 0 ? "outlined" : "filled"}
          />
        </Box>
        <Collapse in={expanded[key]} unmountOnExit>
          <Box sx={{ pl: 5, pr: 1 }}>
            {entries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None.
              </Typography>
            ) : (
              <>
                {shown.map((entry) => renderEntry(entry, isBlockedSet))}
                {overflow > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic" }}>
                    +{overflow} more
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} onClick={(e) => e.stopPropagation()} maxWidth="sm" fullWidth>
      <DialogTitle variant="h3">Delete Document</DialogTitle>
      <DialogContent>
        {!allowCascade ? (
          <DialogContentText>
            {documentName ? (
              <>
                Permanently delete <strong>{documentName}</strong>? It will be removed from Documents.
              </>
            ) : (
              "Permanently delete this document? It will be removed from Documents."
            )}
          </DialogContentText>
        ) : isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity={AlertType.WARNING} sx={{ mb: 2 }}>
            Couldn&apos;t load the delete-impact preview ({error}). You can still delete the document only; the cascade
            option is unavailable until the preview loads.
          </Alert>
        ) : (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              {documentName ? (
                <>
                  Review the impact of deleting <strong>{documentName}</strong>, then choose how to proceed.
                </>
              ) : (
                "Review the impact of deleting this document, then choose how to proceed."
              )}
            </DialogContentText>

            {isBlocked && (
              <Alert severity={AlertType.WARNING} sx={{ mb: 2 }}>
                Some related records block a full cascade delete. You can still delete the document only.
              </Alert>
            )}

            {data && (
              <>
                {renderSection("delete", "Delete", data.delete, data.delete_count, false)}
                {renderSection("keep", "Keep and scrub", data.keep_and_scrub, data.keep_and_scrub_count, false)}
                {renderSection("blocked", "Blocked", data.blocked, data.blocked_count, true)}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  <strong>Delete document only</strong> removes the document itself; any records it created (shown
                  above) remain in the system.
                </Typography>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant={ButtonStyleType.Text} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={handleDeleteDocumentOnly}
          variant={allowCascade ? ButtonStyleType.Outlined : ButtonStyleType.Contained}
          color="error"
          // NOT disabled on a preview error: "Delete document only" is today's
          // single-row delete and needs no impact preview, so it must stay
          // available even when /delete-impact is down or not yet deployed
          // (ADR-078 D9(b) "always enabled"). Only the cascade depends on the preview.
          disabled={isLoading || busy}
        >
          {allowCascade ? "Delete document only" : "Delete document"}
        </Button>
        {allowCascade && (
          <Tooltip title={cascadeDisabledReason ?? ""}>
            {/* span keeps the tooltip working while the button is disabled */}
            <span>
              <Button
                onClick={handleStartCascade}
                variant={ButtonStyleType.Contained}
                color="error"
                disabled={isLoading || busy || !!error || !cascadeAllowed}
              >
                Delete document + related records
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};
